import { useToggle } from "usehooks-ts";
import { useCallback, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import Modal from "components/Modal";
import { ExclamationSVGIcon } from "components/SVGIcons";
import DocumentReviewRequirementModal from "features/main/HaulPay/Shared/EditFundingRequestV2/Manage/DocumentReviewRequirementModal";
import { hasRequiredDocumentReviewDocuments } from "features/main/HaulPay/Shared/EditFundingRequestV2/Manage/documentReviewEligibility";
import { showErrorMessage } from "helpers/toastMessage";
import { IFRAttachment, InformationFormData } from "models/HaulPay";
import styles from "./style.module.scss";
import { MUTATION_SUBMIT_FR_TO_DOCUMENT_REVIEW } from "../query";
import UploadSection from "./UploadSection";
import AttachmentsList from "./AttachmentsList";

interface SharedValue {
  informationFormData?: InformationFormData;
  uploadedFiles: IFRAttachment[];
  createdFR: any;
  handleNext: VoidFunction;
  handleBack: VoidFunction;
  setUploadedFiles: (files: IFRAttachment[]) => void;
}

export default function PayDocuments({
  informationFormData,
  createdFR,
  handleNext,
  handleBack,
  uploadedFiles,
  setUploadedFiles
}: Readonly<SharedValue>) {
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle();
  const [isRateConfirmModalOpen, toggleRateConfirmModal] = useToggle();
  const [isBOLRequiredModalOpen, toggleBOLRequiredModal] = useToggle();
  const [isSubmitConfirmOpen, toggleSubmitConfirmModal] = useToggle();
  const [editingAttachment, setEditingAttachment] = useState<IFRAttachment | null>(null);
  const [submitFRToDocumentReview, { loading: isSubmittingToDocumentReview }] = useMutation(
    MUTATION_SUBMIT_FR_TO_DOCUMENT_REVIEW
  );

  // Pick-Up only. This guards the fuel advance branch, where a delivered BOL is not the document
  // being asked for -- accepting it here let a fuel advance through to a check that then rejected
  // it for the wrong reason (HP-9251).
  const hasSignedBill = useMemo(
    () => uploadedFiles.some((file) => file.category.includes("Signed Bill of Lading (Pick-Up)")),
    [uploadedFiles]
  );

  // HP-8994: a carrier's uploads no longer transition the request on the backend, so finishing the
  // wizard in one session is what submits it. Returning to an Incomplete request later goes through
  // the explicit Submit action on the edit screen instead. Both contract types submit here.
  const submitToDocumentReview = useCallback(async () => {
    try {
      const res = await submitFRToDocumentReview({ variables: { id: createdFR.id } });
      if (!res?.data?.submitFundingRequestToDocumentReview?.success) {
        showErrorMessage("Failed to submit the funding request to Document Review.");
        return false;
      }
      return true;
    } catch {
      showErrorMessage("Failed to submit the funding request to Document Review.");
      return false;
    }
  }, [submitFRToDocumentReview, createdFR?.id]);

  const goToConfirmation = useCallback(async () => {
    if (!uploadedFiles.length) {
      toggleConfirmModal();
    } else if (informationFormData?.isFuelAdvance && !hasSignedBill) {
      toggleBOLRequiredModal();
    } else if (
      hasRequiredDocumentReviewDocuments(uploadedFiles, informationFormData?.isFuelAdvance)
    ) {
      if (informationFormData?.isNonFactoredInvoice) {
        toggleSubmitConfirmModal();
      } else {
        const didSubmit = await submitToDocumentReview();
        if (didSubmit) {
          handleNext();
        }
      }
    } else {
      toggleRateConfirmModal();
    }
  }, [
    uploadedFiles,
    hasSignedBill,
    informationFormData?.isFuelAdvance,
    informationFormData?.isNonFactoredInvoice,
    submitToDocumentReview,
    handleNext,
    toggleConfirmModal,
    toggleBOLRequiredModal,
    toggleSubmitConfirmModal,
    toggleRateConfirmModal
  ]);

  const resetData = () => {
    toggleConfirmModal();
    setUploadedFiles([]);
    handleBack();
  };

  return (
    <div className={styles.payDocumentsContainer}>
      <div className={styles.body}>
        <h4 className={styles.header}>Documents</h4>
        <div className={styles.uploadWrapper}>
          <UploadSection
            fundingRequestId={createdFR.id}
            attachments={uploadedFiles}
            onAttachmentsChange={setUploadedFiles}
            editingAttachment={editingAttachment}
            setEditingAttachment={setEditingAttachment}
          />
          <AttachmentsList
            attachments={uploadedFiles}
            onAttachmentsChange={setUploadedFiles}
            fundingRequestId={createdFR.id}
            onEditAttachment={setEditingAttachment}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          variant="outline"
          onClick={toggleConfirmModal}
          disabled={isSubmittingToDocumentReview}>
          Complete Later
        </Button>
        <Button onClick={goToConfirmation} disabled={isSubmittingToDocumentReview}>
          Next
        </Button>
      </div>

      <Modal
        isOpen={isConfirmModalOpen}
        closeModal={toggleConfirmModal}
        wrapperClass="!my-auto desktop:!min-h-fit desktop:!w-1/2 text-sm"
        dialogClass="!bg-foreground !px-3 !py-4 gap-2.5"
        closeClass="!top-24 !right-6 !bg-transparent !text-text">
        <div className="flex items-center gap-1 text-xl font-bold text-primary">
          <ExclamationSVGIcon />
          <span>Confirm</span>
        </div>
        <p className="pt-1.5 font-medium">
          Are you sure you want to create an Incomplete Funding Request? You can navigate to
          Incomplete in Manage Pay to add documents later if you do.
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            rounded
            className="!w-20 text-sm !h-9 self-end"
            onClick={toggleConfirmModal}>
            No
          </Button>
          <Button rounded className="!w-20 text-sm !h-9 self-end" onClick={resetData}>
            Yes
          </Button>
        </div>
      </Modal>

      <DocumentReviewRequirementModal
        isOpen={isRateConfirmModalOpen}
        onClose={toggleRateConfirmModal}
        attachments={uploadedFiles}
        isFuelAdvance={Boolean(informationFormData?.isFuelAdvance)}
      />

      <ConfirmationModal
        title="Submit to Doc Review?"
        toggleConfirmModal={toggleSubmitConfirmModal}
        isConfirmModalOpen={isSubmitConfirmOpen}
        isLoading={isSubmittingToDocumentReview}
        content={
          <span className="block text-left">
            This moves the funding request to Document Review so our team can start processing it.
            Once submitted you will no longer be able to change the request or its documents.
          </span>
        }
        labelOKButton="Submit to Doc Review"
        onOK={async () => {
          const didSubmit = await submitToDocumentReview();
          if (didSubmit) {
            toggleSubmitConfirmModal();
            handleNext();
          }
        }}
      />

      <ConfirmationModal
        content={
          "Since a fuel advance request has been submitted, a BOL (Bill of Lading) pick-up document is required."
        }
        isHideCancelButton
        isConfirmModalOpen={isBOLRequiredModalOpen}
        toggleConfirmModal={toggleBOLRequiredModal}
      />
    </div>
  );
}
